import type { ControlActionParams } from '@lighthouse/core'
import { ActionControlType } from '@lighthouse/core'
import type { ActionFlowNode, FlowNodeResultADT } from '@lighthouse/shared'
import {
    blockListenerSystem,
    generateVariableValue,
    getBlockAncestors,
    getBlockListenerKey,
    getWithScopeId,
    isCustomViewBlock,
    resolveControlRecordId,
    resolveNthPanelParam
} from '@lighthouse/shared'

import type { ExecuteActionParams } from '../type'

type ControlConfig = {
    controlElementId?: string
    controlElementType?: 'view' | 'container' | 'field'
    controlAction?: ControlActionParams
}

const controlCore = (config: ControlConfig | undefined, runtimeParams: ExecuteActionParams): FlowNodeResultADT => {
    const { controlElementId, controlElementType, controlAction } = config || {}
    const { scope, blockId, viewRecord, pageBlockContextIndex, customViewStack } = runtimeParams
    if (!controlElementId || !controlElementType || !controlAction?.type) {
        return {
            type: 'interrupted',
            success: true
        }
    }

    const transformPayload = (payload: ControlActionParams) => {
        if (!('params' in payload)) {
            return undefined
        }
        if (payload.type === 'scrollToRecord') {
            const resolved = generateVariableValue({
                innerType: 'TEXT',
                jsonContent: payload.params.scrollToRecord,
                extraParams: runtimeParams
            }) as string
            return { scrollToRecord: resolved }
        }
        if (payload.type === ActionControlType.SWITCH_PANEL) {
            if (payload.params.switchPanelId !== 'nthPanel') {
                return {
                    switchPanelId: payload.params.switchPanelId,
                    autoForwardLoop: payload.params.autoForwardLoop,
                    loopInterval: payload.params.loopInterval
                }
            }
            return {
                ...payload.params,
                nthPanel: resolveNthPanelParam(payload.params.nthPanel, runtimeParams)
            }
        }
        return payload.params
    }

    const parentCustomViewOfControlElement = pageBlockContextIndex?.contextMap.get(getWithScopeId(controlElementId, scope))?.customView
    const triggerElementContext = blockId ? pageBlockContextIndex?.contextMap.get(getWithScopeId(blockId, scope)) : undefined
    const parentCustomViewOfTriggerElement = triggerElementContext
        ? isCustomViewBlock(triggerElementContext.block)
            ? triggerElementContext.block
            : triggerElementContext.customView
        : undefined

    const controlElementKey = getWithScopeId(controlElementId, scope)
    const ancestorCustomViewIds = pageBlockContextIndex
        ? getBlockAncestors(controlElementKey, pageBlockContextIndex)
              .filter(context => isCustomViewBlock(context.block))
              .map(context => context.block.id)
        : undefined
    const listenerKey = getBlockListenerKey(
        controlElementId,
        scope,
        resolveControlRecordId({
            controlElementId,
            controlElementType,
            viewRecordId: viewRecord?.id,
            customViewStack,
            ancestorCustomViewIds,
            parentCustomViewIdOfControl: parentCustomViewOfControlElement?.id,
            parentCustomViewIdOfTrigger: parentCustomViewOfTriggerElement?.id
        })
    )
    blockListenerSystem.emit(listenerKey, controlElementType, {
        event: controlAction.type,
        params: transformPayload(controlAction)
    })

    return {
        type: 'record',
        success: true
    }
}

// list 独立动作使用
export const handleControlList = (config: ControlConfig | undefined, runtimeParams: ExecuteActionParams): Promise<FlowNodeResultADT> => {
    return Promise.resolve(controlCore(config, runtimeParams))
}

// 预留 stream 形态（flow 如需）
export const handleControlStream = (
    node: ActionFlowNode<{ config: ControlConfig }>,
    runtimeParams: ExecuteActionParams
): Promise<FlowNodeResultADT> => {
    return Promise.resolve(controlCore(node.data.config, runtimeParams))
}
